import React, { useEffect } from "react";
import { useQuery } from "@apollo/client";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezonePlugin from "dayjs/plugin/timezone";
import { Header, HeaderProps, ThemeMode } from "../../sandbox/components";
import { useTheme } from "../../hooks/useTheme";
import { useTimezoneStore } from "../../store/timezoneStore";
import { GET_TIMEZONE_INFO } from "../../api/graphql/users";
import BalanceCard from "../BalanceCard/HeaderBalanceCard";
import { NotificationModal } from "../Modals/NotificationModal";
import { UserMenu } from "../Header/UserMenu";
import { ReturnToAdminButton } from "../Header/ReturnToAdminButton";
import { McpStatus } from "../Header/McpStatus";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

// app theme uses light|auto|dark; the DS Header uses light|system|dark
const toDsTheme = (t: string): ThemeMode =>
  t === "auto" ? "system" : (t as ThemeMode);
const toAppTheme = (t: ThemeMode) => (t === "system" ? "auto" : t);

const getOffsetMinutes = (utcString?: string) => {
  if (!utcString) return 0;
  const m = utcString.match(/([+-])\s*(\d{1,2})(?::?(\d{2}))?/i);
  if (!m) return 0;
  const sign = m[1] === "-" ? -1 : 1;
  return sign * (parseInt(m[2], 10) * 60 + (m[3] ? parseInt(m[3], 10) : 0));
};

/**
 * App container: wires the design-system Header to real data, theme and
 * sub-features. On a phone it is the top bar: `onMenu` opens the drawer.
 */
const AppHeader: React.FC<Pick<HeaderProps, "onMenu" | "menuOpen">> = ({
  onMenu,
  menuOpen,
}) => {
  const { data } = useQuery(GET_TIMEZONE_INFO);
  const { theme, setTheme } = useTheme();
  // The bar no longer shows the clock, but the order flows read the profile
  // timezone from this store — keep it ticking.
  const { updateTimezone, updateLocalTime } = useTimezoneStore();

  useEffect(() => {
    updateTimezone({ ...data?.getProfileTimezone });
    let interval: ReturnType<typeof setInterval>;
    if (data?.getProfileTimezone) {
      const offset = getOffsetMinutes(data?.getProfileTimezone?.timezone);
      interval = setInterval(() => {
        updateLocalTime(dayjs().utcOffset(offset).format("HH:mm"));
      }, 1000);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <Header
      // No clock in the bar: the MCP widget holds the left. The timezone
      // itself still drives the order flows through the store above.
      theme={toDsTheme(theme)}
      onThemeChange={(t) => setTheme(toAppTheme(t))}
      mcpSlot={<McpStatus />}
      leadingSlot={<ReturnToAdminButton />}
      walletSlot={<BalanceCard />}
      bellSlot={<NotificationModal />}
      avatarSlot={<UserMenu />}
      onMenu={onMenu}
      menuOpen={menuOpen}
    />
  );
};

export default AppHeader;
